import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/base/SEOHead";
import { getComicById, getRelatedComics } from "@/mocks/comics";
import type { ComicArticle } from "@/mocks/comics";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || "");
  const [isReading, setIsReading] = useState(false);
  const [activeChapter, setActiveChapter] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    setIsReading(false);
    setActiveChapter(0);
  }, [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <h2 className="text-xl font-display text-foreground-700 mt-4">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mt-2">该漫画可能已被移除或链接无效</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block font-medium">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "ComicStory",
    name: comic.title,
    author: {
      "@type": "Person",
      name: comic.author,
    },
    description: comic.description,
    genre: comic.category,
    datePublished: "2026-01-01",
    dateModified: comic.updateDate,
    url: `https://feimagua.com/article/${comic.id}`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: comic.rating,
      bestRating: "10",
      ratingCount: comic.views,
    },
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: "https://schema.org/ReadAction",
      userInteractionCount: comic.views,
    },
    keywords: comic.tags.join(","),
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 非麻瓜漫画_免费韩漫下拉式阅读`}
        description={comic.description}
        keywords={`${comic.title},${comic.tags.join(",")},非麻瓜漫画,免费韩漫,下拉式观看`}
        canonicalUrl={`https://feimagua.com/article/${comic.id}`}
        ogType="article"
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {!isReading ? (
          <main className="pt-16">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            <article className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-[300px] flex-shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200 sticky top-20">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} 封面`}
                      title={`${comic.title} - 免费韩漫在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium whitespace-nowrap">
                      {comic.category}
                    </span>
                    {comic.isCompleted && (
                      <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-sm font-medium whitespace-nowrap">
                        已完结
                      </span>
                    )}
                    {comic.isHot && (
                      <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium whitespace-nowrap">
                        热门
                      </span>
                    )}
                    {comic.isNew && (
                      <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap">
                        NEW
                      </span>
                    )}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-display text-foreground-950 mb-2">
                    {comic.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-4">
                    作者：<strong>{comic.author}</strong>
                    <span className="mx-2">|</span>
                    更新日期：{comic.updateDate}
                    <span className="mx-2">|</span>
                    状态：{comic.isCompleted ? "已完结" : "连载中"}
                  </p>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-lg"></i>
                      <span className="text-lg font-semibold text-foreground-900">{comic.rating}</span>
                      <span className="text-xs text-foreground-500">/ 10</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-eye-line"></i>
                      <span className="text-sm">{comic.views.toLocaleString()} 次阅读</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-file-list-3-line"></i>
                      <span className="text-sm">共 {comic.chapters.length} 话</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6 bg-background-100/70 rounded-xl p-4">
                    {comic.description}
                  </p>

                  <div className="flex items-center gap-3 mb-8">
                    <button
                      onClick={() => setIsReading(true)}
                      className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      开始阅读（第1话）
                    </button>
                    <button className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 hover:bg-secondary-200 transition-colors cursor-pointer">
                      <i className="ri-bookmark-line"></i>
                    </button>
                    <button className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 hover:bg-secondary-200 transition-colors cursor-pointer">
                      <i className="ri-heart-line"></i>
                    </button>
                  </div>

                  <div>
                    <h2 className="text-lg font-display text-foreground-950 mb-4">
                      章节目录
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {comic.chapters.map((ch, idx) => (
                        <button
                          key={ch.id}
                          onClick={() => {
                            setActiveChapter(idx);
                            setIsReading(true);
                          }}
                          className="text-left px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer whitespace-nowrap truncate"
                        >
                          <span className="text-foreground-400 text-xs mr-2">
                            {ch.index}
                          </span>
                          {ch.title}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {relatedComics.length > 0 && (
                <section className="mt-12 pt-8 border-t border-background-200/70">
                  <h2 className="text-lg font-display text-foreground-950 mb-4">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                    {relatedComics.map((rc) => (
                      <Link
                        key={rc.id}
                        to={`/article/${rc.id}`}
                        className="group cursor-pointer"
                        data-product-shop=""
                      >
                        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2">
                          <img
                            src={rc.coverImage}
                            alt={rc.title}
                            title={`${rc.title} - 免费韩漫在线阅读`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                          {rc.title}
                        </h3>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </main>
        ) : (
          <main className="pt-16">
            <div className="max-w-2xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between mb-4 sticky top-16 z-20 bg-background-50/95 backdrop-blur-sm py-3 -mx-4 px-4">
                <button
                  onClick={() => setIsReading(false)}
                  className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回详情
                </button>
                <div className="text-sm font-medium text-foreground-900">
                  {comic.chapters[activeChapter]?.title || "阅读中"}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setActiveChapter(Math.max(0, activeChapter - 1))}
                    disabled={activeChapter === 0}
                    className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-50 hover:text-primary-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-up-s-line"></i>
                  </button>
                  <button
                    onClick={() => setActiveChapter(Math.min(comic.chapters.length - 1, activeChapter + 1))}
                    disabled={activeChapter >= comic.chapters.length - 1}
                    className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-50 hover:text-primary-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-down-s-line"></i>
                  </button>
                </div>
              </div>

              <div className="mb-6 text-center">
                <h1 className="text-lg font-display text-foreground-950">
                  {comic.title}
                </h1>
                <p className="text-sm text-primary-500 font-medium mt-1">
                  {comic.chapters[activeChapter]?.title}
                </p>
              </div>

              <div className="space-y-0">
                {comic.content.map((_, idx) => (
                  <div
                    key={idx}
                    className="w-full aspect-[3/4] bg-background-200 rounded-lg mb-4 flex items-center justify-center"
                  >
                    <div className="text-center text-foreground-400">
                      <i className="ri-image-line text-4xl block mb-2"></i>
                      <span className="text-sm">
                        漫画内页 {idx + 1} - 下拉式阅读模式
                      </span>
                      <p className="text-xs mt-1 text-foreground-300">
                        （实际使用时此处为漫画原图）
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-center gap-3 py-6">
                <button
                  onClick={() => setActiveChapter(Math.max(0, activeChapter - 1))}
                  disabled={activeChapter === 0}
                  className="px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  上一话
                </button>
                <span className="text-sm text-foreground-500">
                  {activeChapter + 1} / {comic.chapters.length}
                </span>
                <button
                  onClick={() => setActiveChapter(Math.min(comic.chapters.length - 1, activeChapter + 1))}
                  disabled={activeChapter >= comic.chapters.length - 1}
                  className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  下一话
                </button>
              </div>
            </div>
          </main>
        )}

        <Footer />
      </div>
    </>
  );
}